/* eslint-env browser */
// Links in the public header (landing, login, about, contact, policies).
import React from "react";
import { makeStyles } from "@mui/styles";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { Info, AccountBox, House } from "@mui/icons-material";
import TelegramIcon from "@mui/icons-material/Telegram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import EmailIcon from '@mui/icons-material/Email';
import TikTokIcon from "../TikTokIcon";
import styles from '../../styles/headerLinksStyle.js';
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import LanguageMenu from "./LanguageMenu";

const useStyles = makeStyles(styles);

// Sentence-case links, 15 px, 44-px targets; full-width rows in the phone drawer.
const linkSx = {
  color: "inherit",
  minHeight: 44,
  px: 1.5,
  fontSize: 15,
  fontWeight: 500,
  borderRadius: "8px",
  justifyContent: { xs: "flex-start", md: "center" },
  width: { xs: "calc(100% - 30px)", md: "auto" },
  ml: { xs: "15px", md: 0 },
  my: { xs: 1, md: 0 },
  "&:hover": { bgcolor: "rgba(200, 200, 200, 0.2)" },
};

const socialSx = {
  color: "inherit",
  width: 44,
  height: 44,
  ml: { xs: "15px", md: 0 },
  "&:hover": { bgcolor: "rgba(200, 200, 200, 0.2)" },
};

export default function HeaderLinks() {
  const classes = useStyles();
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loggedIn = Boolean(auth.profile && auth.profile.uid);
  const tipPlacement = window.innerWidth > 959 ? "top" : "left";

  const socials = [
    { key: "telegram", url: settings && settings.TelegramHandle, title: t('follow_telegram'), icon: <TelegramIcon /> },
    {
      key: "tiktok",
      url: settings && settings.TikTokHandle,
      title: t('follow_tiktok'),
      icon: <TikTokIcon />,
    },
    { key: "youtube", url: settings && settings.YouTubeHandle, title: t('follow_youtube'), icon: <YouTubeIcon /> },
    { key: "facebook", url: settings && settings.FacebookHandle, title: t('follow_facebook'), icon: <FacebookIcon /> },
    { key: "instagram", url: settings && settings.InstagramHandle, title: t('follow_instagram'), icon: <InstagramIcon /> },
  ].filter((s) => s.url);

  return (
    <List className={classes.list}>
      <ListItem className={classes.listItem}>
        <Button sx={linkSx} startIcon={<House />} onClick={(e) => { e.preventDefault(); navigate('/'); }}>
          {t('home')}
        </Button>
      </ListItem>
      <ListItem className={classes.listItem}>
        <Button
          sx={linkSx}
          startIcon={<AccountBox />}
          onClick={(e) => {
            e.preventDefault();
            navigate(loggedIn ? '/bookings' : '/login');
          }}
        >
          {loggedIn ? t('myaccount') : t('login_signup')}
        </Button>
      </ListItem>
      <ListItem className={classes.listItem}>
        <Button sx={linkSx} startIcon={<Info />} onClick={(e) => { e.preventDefault(); navigate('/about-us'); }}>
          {t('about_us')}
        </Button>
      </ListItem>
      <ListItem className={classes.listItem}>
        <Button sx={linkSx} startIcon={<EmailIcon />} onClick={(e) => { e.preventDefault(); navigate('/contact-us'); }}>
          {t('contact_us')}
        </Button>
      </ListItem>
      {socials.map((s) => (
        <ListItem className={classes.listItem} key={s.key}>
          <Tooltip id={`${s.key}-link`} title={s.title} placement={tipPlacement}>
            <IconButton href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.title} sx={socialSx}>
              {s.icon}
            </IconButton>
          </Tooltip>
        </ListItem>
      ))}
      <ListItem className={classes.listItem}>
        <LanguageMenu variant="header" sx={linkSx} />
      </ListItem>
    </List>
  );
}
